import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAccessRolStore } from '../stores';
import type {
  AccessRolAction,
  AccessRolType,
} from '../types/accessRol';
import type { ParamsHasAccessRol, UseAccessReturn } from '../types/useAccessRol.type';
import { useAccessExclusions } from './useAccessExclusions';

export function useAccess({
  section,
  actions,
}: ParamsHasAccessRol): UseAccessReturn {
  const accessRolStore = useAccessRolStore();
  const { isSectionExcluded, isActionExcluded } = useAccessExclusions();

  const { accessRol, isLoading, isLoaded } = storeToRefs(accessRolStore);

  const sectionAccessRol = computed(() =>
    accessRol.value?.find((p: AccessRolType) => p.section === section)
  );

  const accessRolLoaded = computed(() => isLoaded.value);

  const hasAccess = computed((): boolean => {
    if (isSectionExcluded(section)) return false;

    const requestedActions = Array.isArray(actions) ? actions : [];

    if (!requestedActions.length) return accessRolStore.hasAccess(section, actions);

    const allowedActions = requestedActions.filter(
      (action) => !isActionExcluded(section, action)
    );

    if (!allowedActions.length) return false;

    return accessRolStore.hasAccess(section, allowedActions);
  });

  const actionsAccess = computed(() => {
    const result: Partial<Record<AccessRolAction, boolean>> = {};
    const requestedActions = Array.isArray(actions) ? actions : [];
    const currentSection = sectionAccessRol.value;

    for (const action of requestedActions) {
      result[action] =
        !isActionExcluded(section, action) &&
        Boolean(currentSection?.actions.includes(action));
    }

    return result;
  });

  return {
    hasAccess,
    accessRolLoaded,
    isLoading,
    actionsAccess,
  };
}
